import {
  createDisposable,
  Disposable,
  disposedDisposable,
} from "./Disposable";

export type EventMap = object;
export type EventName<Events extends EventMap> = Extract<keyof Events, string>;
export type TypedEventListener<Payload> = (payload: Payload) => void;

export interface EventSubscriptionOptions {
  once?: boolean;
  signal?: AbortSignal;
}

type ListenerRecord<Payload> = {
  listener: TypedEventListener<Payload>;
  once: boolean;
  subscription: Disposable;
};

export class TypedEventHub<Events extends EventMap> implements Disposable {
  private readonly listeners = new Map<
    EventName<Events>,
    Set<ListenerRecord<unknown>>
  >();
  private _disposed = false;

  get disposed(): boolean {
    return this._disposed;
  }

  on<Name extends EventName<Events>>(
    name: Name,
    listener: TypedEventListener<Events[Name]>,
    options: EventSubscriptionOptions = {},
  ): Disposable {
    this.assertActive();
    if (options.signal?.aborted) return disposedDisposable();

    let records = this.listeners.get(name);
    if (!records) {
      records = new Set();
      this.listeners.set(name, records);
    }

    let abortHandler: (() => void) | undefined;
    const record = {
      listener: listener as TypedEventListener<unknown>,
      once: options.once ?? false,
      subscription: undefined as unknown as Disposable,
    };
    const subscription = createDisposable(() => {
      records!.delete(record);
      if (records!.size === 0) this.listeners.delete(name);
      if (abortHandler && options.signal) {
        options.signal.removeEventListener("abort", abortHandler);
      }
    });
    record.subscription = subscription;
    records.add(record);

    if (options.signal) {
      abortHandler = () => subscription.dispose();
      options.signal.addEventListener("abort", abortHandler, { once: true });
    }

    return subscription;
  }

  once<Name extends EventName<Events>>(
    name: Name,
    listener: TypedEventListener<Events[Name]>,
    options: Omit<EventSubscriptionOptions, "once"> = {},
  ): Disposable {
    return this.on(name, listener, { ...options, once: true });
  }

  emit<Name extends EventName<Events>>(
    name: Name,
    ...args: Events[Name] extends void ? [] : [payload: Events[Name]]
  ): void {
    if (this._disposed) return;
    const records = this.listeners.get(name);
    if (!records?.size) return;

    const payload = args[0] as Events[Name];
    const errors: unknown[] = [];
    for (const record of [...records]) {
      if (record.subscription.disposed) continue;
      if (record.once) record.subscription.dispose();
      try {
        (record.listener as TypedEventListener<Events[Name]>)(payload);
      } catch (error) {
        errors.push(error);
      }
    }

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, `Listeners for "${name}" failed`);
    }
  }

  listenerCount<Name extends EventName<Events>>(name: Name): number {
    return this.listeners.get(name)?.size ?? 0;
  }

  clear<Name extends EventName<Events>>(name?: Name): void {
    if (name !== undefined) {
      this.disposeRecords(this.listeners.get(name));
      return;
    }
    for (const records of [...this.listeners.values()]) {
      this.disposeRecords(records);
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this.clear();
    this._disposed = true;
  }

  private disposeRecords(
    records: Set<ListenerRecord<unknown>> | undefined,
  ): void {
    if (!records) return;
    for (const record of [...records]) record.subscription.dispose();
  }

  private assertActive(): void {
    if (this._disposed) {
      throw new Error("TypedEventHub has been disposed");
    }
  }
}
